import { useCallback, useEffect, useState } from 'react'

// Per-browser "done" state for the speaker. The shared page has no login and is
// read-only against the API, so progress lives in localStorage instead — keyed
// by mode id so it survives reloads and even regenerated share links.
const PREFIX = 'voiceover-done:v1:'

export type DoneState = {
  isDone: (id: string) => boolean
  toggle: (id: string) => void
  setMany: (ids: string[], value: boolean) => void
  doneCount: number
}

export function useDoneState (storageKey: string): DoneState {
  const key = PREFIX + storageKey
  const [done, setDone] = useState<Set<string>>(new Set())

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key)
      setDone(new Set(raw ? JSON.parse(raw) : []))
    } catch {
      setDone(new Set())
    }
  }, [key])

  const persist = (next: Set<string>) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(Array.from(next)))
    } catch {
      /* ignore quota / disabled storage */
    }
  }

  const toggle = useCallback(
    (id: string) => {
      setDone((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        persist(next)
        return next
      })
    },
    [key]
  )

  // Mark/unmark a whole batch (e.g. an entire chapter) in one go.
  const setMany = useCallback(
    (ids: string[], value: boolean) => {
      setDone((prev) => {
        const next = new Set(prev)
        ids.forEach((id) => {
          if (value) {
            next.add(id)
          } else {
            next.delete(id)
          }
        })
        persist(next)
        return next
      })
    },
    [key]
  )

  const isDone = useCallback((id: string) => done.has(id), [done])

  return { isDone, toggle, setMany, doneCount: done.size }
}
